import React, { useState, useEffect } from 'react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { barrelService } from '../../services/barrelService';
import { cartonService } from '../../services/cartonService';
import { supabase } from '../../lib/supabase';
import { ShoppingCart, Plus, Trash2, Search, RefreshCw, X, AlertTriangle, CheckCircle, Wine, Package, FileText, ChevronDown, ChevronUp } from 'lucide-react';

const PAYMENT_STATUS_LABELS = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700' },
  paid: { label: 'Payé', color: 'bg-green-100 text-green-700' },
  partial: { label: 'Partiel', color: 'bg-blue-100 text-blue-700' },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700' }
};

const BARREL_TYPES = [
  { value: 'noir_270l', label: 'Barrique Noire 270L', defaultPrice: 35 },
  { value: 'bleu_220l', label: 'Barrique Bleue 220L', defaultPrice: 30 }
];

const defaultOrderLine = () => ({
  id: Date.now() + Math.random(),
  itemType: 'barrel', // 'barrel' | 'carton'
  barrelType: 'noir_270l',
  cartonInventoryId: '',
  cartonType: '',
  quantity: 1,
  unitPrice: 35,
  stock: 0
});

const defaultForm = {
  customerName: '',
  customerPhone: '',
  customerEmail: '',
  paymentStatus: 'pending',
  saleDate: new Date()?.toISOString()?.split('T')?.[0],
  notes: '',
  lines: [defaultOrderLine()]
};

export default function UnifiedSalesManagement() {
  const [sales, setSales] = useState([]);
  const [barrelInventory, setBarrelInventory] = useState([]);
  const [cartonInventory, setCartonInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedSale, setExpandedSale] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [barrelSales, cartonSales, barrelInv, cartonInv] = await Promise.all([
        barrelService?.getBarrelOrders(),
        cartonService?.getCartonSales(),
        barrelService?.getInventory(),
        cartonService?.getCartonInventory()
      ]);

      // Merge and normalize sales
      const merged = [
        ...(barrelSales || [])?.map(s => ({
          ...s,
          _type: 'barrel',
          reference: s?.reference || s?.id,
          customerName: s?.clientName || s?.customer_name || '',
          customerPhone: s?.clientPhone || s?.client_phone || '',
          paymentStatus: s?.paymentStatus || s?.payment_status || 'pending',
          saleDate: s?.saleDate || s?.sale_date || s?.created_at?.split('T')?.[0],
          totalPrice: s?.totalPrice || s?.total_price || 0,
          lines: [{
            itemType: 'barrel',
            barrelType: s?.barrelType || s?.barrel_type,
            label: BARREL_TYPES?.find(b => b?.value === (s?.barrelType || s?.barrel_type))?.label || s?.barrelType || s?.barrel_type,
            quantity: s?.quantity || 1,
            unitPrice: s?.unitPrice || s?.unit_price || 0,
            total: s?.totalPrice || s?.total_price || 0
          }]
        })),
        ...(cartonSales || [])?.map(s => ({
          ...s,
          _type: 'carton',
          reference: s?.reference || s?.id,
          customerName: s?.customerName || '',
          customerPhone: s?.customerPhone || '',
          paymentStatus: s?.paymentStatus || 'pending',
          saleDate: s?.saleDate || s?.created_at?.split('T')?.[0],
          totalPrice: s?.totalPrice || 0,
          lines: [{
            itemType: 'carton',
            cartonType: s?.cartonType,
            label: s?.cartonType,
            quantity: s?.quantity || 1,
            unitPrice: s?.unitPrice || 0,
            total: s?.totalPrice || 0
          }]
        }))
      ]?.sort((a, b) => new Date(b?.saleDate || b?.created_at) - new Date(a?.saleDate || a?.created_at));

      setSales(merged);
      setBarrelInventory(barrelInv || []);
      setCartonInventory(cartonInv || []);
    } catch (e) {
      console.error(e);
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const getBarrelStock = (barrelType) => {
    const item = barrelInventory?.find(i => i?.barrelType === barrelType || i?.barrel_type === barrelType);
    return item?.stockQuantity || item?.stock_quantity || 0;
  };

  const getCartonStock = (cartonInventoryId) => {
    const item = cartonInventory?.find(i => i?.id === cartonInventoryId);
    return item?.stockQuantity || 0;
  };

  const updateLine = (lineId, field, value) => {
    setForm(f => ({
      ...f,
      lines: f?.lines?.map(l => {
        if (l?.id !== lineId) return l;
        const updated = { ...l, [field]: value };
        if (field === 'itemType') {
          if (value === 'barrel') {
            updated.barrelType = 'noir_270l';
            updated.unitPrice = 35;
            updated.cartonInventoryId = '';
            updated.cartonType = '';
          } else {
            updated.barrelType = '';
            updated.unitPrice = 0;
          }
        }
        if (field === 'barrelType') {
          const bt = BARREL_TYPES?.find(b => b?.value === value);
          updated.unitPrice = bt?.defaultPrice || 0;
        }
        if (field === 'cartonInventoryId') {
          const ci = cartonInventory?.find(i => i?.id === value);
          updated.cartonType = ci?.cartonType || '';
          updated.unitPrice = ci?.unitPriceSell || 0;
        }
        return updated;
      })
    }));
  };

  const addLine = () => {
    setForm(f => ({ ...f, lines: [...f?.lines, defaultOrderLine()] }));
  };

  const removeLine = (lineId) => {
    setForm(f => ({ ...f, lines: f?.lines?.filter(l => l?.id !== lineId) }));
  };

  const orderTotal = form?.lines?.reduce((sum, l) => sum + (l?.quantity * l?.unitPrice), 0);

  const handleSave = async () => {
    if (!form?.customerName || form?.lines?.length === 0) {
      setError('Veuillez renseigner le nom du client et au moins un article.');
      return;
    }
    // Validate stock
    for (const line of form?.lines) {
      if (line?.itemType === 'barrel') {
        const stock = getBarrelStock(line?.barrelType);
        if (line?.quantity > stock) {
          setError(`Stock insuffisant pour ${BARREL_TYPES?.find(b => b?.value === line?.barrelType)?.label} (stock: ${stock})`);
          return;
        }
      } else {
        const stock = getCartonStock(line?.cartonInventoryId);
        if (line?.quantity > stock) {
          setError(`Stock insuffisant pour ${line?.cartonType} (stock: ${stock})`);
          return;
        }
      }
    }

    setSaving(true);
    setError('');
    try {
      // Create one sale per line (existing services handle stock deduction)
      for (const line of form?.lines) {
        if (line?.itemType === 'barrel') {
          await barrelService?.createSale({
            clientName: form?.customerName,
            clientPhone: form?.customerPhone,
            barrelType: line?.barrelType,
            quantity: line?.quantity,
            unitPrice: line?.unitPrice,
            totalPrice: line?.quantity * line?.unitPrice,
            paymentStatus: form?.paymentStatus,
            orderStatus: 'pending',
            notes: form?.notes
          });
        } else {
          await cartonService?.createCartonSale({
            customerName: form?.customerName,
            customerPhone: form?.customerPhone,
            customerEmail: form?.customerEmail,
            cartonType: line?.cartonType,
            cartonInventoryId: line?.cartonInventoryId,
            quantity: line?.quantity,
            unitPrice: line?.unitPrice,
            paymentStatus: form?.paymentStatus,
            saleDate: form?.saleDate,
            notes: form?.notes
          });
        }
      }
      setShowModal(false);
      setForm(defaultForm);
      setSuccess('Commande enregistrée avec succès ! Stock mis à jour automatiquement.');
      fetchData();
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      console.error(e);
      setError('Erreur lors de la sauvegarde : ' + (e?.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePayment = async (sale) => {
    const newStatus = sale?.paymentStatus === 'paid' ? 'pending' : 'paid';
    try {
      if (sale?._type === 'barrel') {
        await supabase?.from('barrel_sales')?.update({ payment_status: newStatus })?.eq('id', sale?.id);
      } else {
        await cartonService?.updateCartonSalePayment(sale?.id, newStatus);
      }
      fetchData();
    } catch (e) {
      setError('Erreur lors de la mise à jour du paiement');
    }
  };

  const handleDelete = async (sale) => {
    if (!window.confirm('Supprimer cette vente ? Le stock sera restauré.')) return;
    try {
      if (sale?._type === 'barrel') {
        await supabase?.from('barrel_sales')?.delete()?.eq('id', sale?.id);
      } else {
        await cartonService?.deleteCartonSale(sale?.id);
      }
      fetchData();
    } catch (e) {
      setError('Erreur lors de la suppression');
    }
  };

  const filtered = sales?.filter(s => {
    const matchSearch = s?.customerName?.toLowerCase()?.includes(search?.toLowerCase()) ||
      String(s?.reference)?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = !filterStatus || s?.paymentStatus === filterStatus;
    return matchSearch && matchStatus;
  });

  const totalRevenue = sales?.filter(s => s?.paymentStatus === 'paid')?.reduce((sum, s) => sum + (s?.totalPrice || 0), 0);
  const pendingCount = sales?.filter(s => s?.paymentStatus === 'pending')?.length;
  const barrelSalesCount = sales?.filter(s => s?._type === 'barrel')?.length;
  const cartonSalesCount = sales?.filter(s => s?._type === 'carton')?.length;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <ShoppingCart className="h-7 w-7 text-green-600" />
              Vente Barriques & Cartons
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Commande unifiée — barriques et cartons sur une seule facture</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700">
              <RefreshCw className="h-4 w-4" /> Actualiser
            </button>
            <button onClick={() => { setForm(defaultForm); setError(''); setShowModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium">
              <Plus className="h-4 w-4" /> Nouvelle commande
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {error}
            <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
            <CheckCircle className="h-4 w-4 flex-shrink-0" /> {success}
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">Total ventes</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{sales?.length}</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">CA encaissé</p>
            <p className="text-2xl font-bold text-green-600">{totalRevenue?.toFixed(2)} €</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
            <div className="flex items-center gap-1 mb-1">
              <Wine className="h-3.5 w-3.5 text-amber-600" />
              <p className="text-xs text-gray-500 dark:text-gray-400">Barriques</p>
            </div>
            <p className="text-2xl font-bold text-amber-600">{barrelSalesCount}</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
            <div className="flex items-center gap-1 mb-1">
              <Package className="h-3.5 w-3.5 text-blue-600" />
              <p className="text-xs text-gray-500 dark:text-gray-400">Cartons</p>
            </div>
            <p className="text-2xl font-bold text-blue-600">{cartonSalesCount}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input type="text" placeholder="Rechercher par client ou référence..." value={search} onChange={e => setSearch(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)}
            className="border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white">
            <option value="">Tous les statuts</option>
            {Object.entries(PAYMENT_STATUS_LABELS)?.map(([k, v]) => <option key={k} value={k}>{v?.label}</option>)}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">Chargement...</div>
          ) : filtered?.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">Aucune vente trouvée</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
                  <tr>
                    <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Référence</th>
                    <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Client</th>
                    <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Type</th>
                    <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Articles</th>
                    <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Total</th>
                    <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Date</th>
                    <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Paiement</th>
                    <th className="text-center px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {filtered?.map(sale => (
                    <React.Fragment key={`${sale?._type}-${sale?.id}`}>
                      <tr className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                        <td className="px-4 py-3 font-mono text-xs text-gray-600 dark:text-gray-400">{String(sale?.reference)?.slice(0, 12)}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900 dark:text-white">{sale?.customerName}</p>
                          {sale?.customerPhone && <p className="text-xs text-gray-500 dark:text-gray-400">{sale?.customerPhone}</p>}
                        </td>
                        <td className="px-4 py-3">
                          {sale?._type === 'barrel' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                              <Wine className="h-3 w-3" /> Barrique
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                              <Package className="h-3 w-3" /> Carton
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setExpandedSale(expandedSale === `${sale?._type}-${sale?.id}` ? null : `${sale?._type}-${sale?.id}`)}
                            className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 hover:text-green-600"
                          >
                            {sale?.lines?.length} article(s)
                            {expandedSale === `${sale?._type}-${sale?.id}` ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-green-600">{(sale?.totalPrice || 0)?.toFixed(2)} €</td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">{sale?.saleDate}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${PAYMENT_STATUS_LABELS?.[sale?.paymentStatus]?.color}`}>
                            {PAYMENT_STATUS_LABELS?.[sale?.paymentStatus]?.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleUpdatePayment(sale)}
                              title="Basculer paiement"
                              className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded text-xs"
                            >
                              <CheckCircle className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => handleDelete(sale)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedSale === `${sale?._type}-${sale?.id}` && (
                        <tr className="bg-gray-50 dark:bg-slate-700/30">
                          <td colSpan={8} className="px-8 py-3">
                            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                              {sale?.lines?.map((line, i) => (
                                <div key={i} className="flex items-center gap-4">
                                  <span className="font-medium">{line?.label || line?.cartonType || line?.barrelType}</span>
                                  <span>× {line?.quantity}</span>
                                  <span>@ {line?.unitPrice?.toFixed(2)} €</span>
                                  <span className="font-semibold text-green-600">= {(line?.total || line?.quantity * line?.unitPrice)?.toFixed(2)} €</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {/* New Order Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-2xl shadow-xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <FileText className="h-5 w-5 text-green-600" />
                Nouvelle commande — Barriques & Cartons
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>

            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {/* Client info */}
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Informations client</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Nom du client *</label>
                    <input type="text" value={form?.customerName} onChange={e => setForm(f => ({ ...f, customerName: e?.target?.value }))}
                      className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Téléphone</label>
                    <input type="text" value={form?.customerPhone} onChange={e => setForm(f => ({ ...f, customerPhone: e?.target?.value }))}
                      className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Date de vente</label>
                    <input type="date" value={form?.saleDate} onChange={e => setForm(f => ({ ...f, saleDate: e?.target?.value }))}
                      className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Statut paiement</label>
                    <select value={form?.paymentStatus} onChange={e => setForm(f => ({ ...f, paymentStatus: e?.target?.value }))}
                      className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white">
                      {Object.entries(PAYMENT_STATUS_LABELS)?.map(([k, v]) => <option key={k} value={k}>{v?.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Order lines */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Articles commandés</h3>
                  <button onClick={addLine} className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-medium">
                    <Plus className="h-3.5 w-3.5" /> Ajouter un article
                  </button>
                </div>
                <div className="space-y-3">
                  {form?.lines?.map((line, idx) => (
                    <div key={line?.id} className="border border-gray-200 dark:border-slate-600 rounded-lg p-3 bg-white dark:bg-slate-700/50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Article {idx + 1}</span>
                        {form?.lines?.length > 1 && (
                          <button onClick={() => removeLine(line?.id)} className="text-red-400 hover:text-red-600">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Type d'article</label>
                          <select value={line?.itemType} onChange={e => updateLine(line?.id, 'itemType', e?.target?.value)}
                            className="w-full border border-gray-300 dark:border-slate-600 rounded px-2 py-1.5 text-xs bg-white dark:bg-slate-700 text-gray-900 dark:text-white">
                            <option value="barrel">🍷 Barrique</option>
                            <option value="carton">📦 Carton</option>
                          </select>
                        </div>
                        {line?.itemType === 'barrel' ? (
                          <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                              Type barrique (stock: {getBarrelStock(line?.barrelType)})
                            </label>
                            <select value={line?.barrelType} onChange={e => updateLine(line?.id, 'barrelType', e?.target?.value)}
                              className="w-full border border-gray-300 dark:border-slate-600 rounded px-2 py-1.5 text-xs bg-white dark:bg-slate-700 text-gray-900 dark:text-white">
                              {BARREL_TYPES?.map(bt => (
                                <option key={bt?.value} value={bt?.value} disabled={getBarrelStock(bt?.value) === 0}>
                                  {bt?.label} {getBarrelStock(bt?.value) === 0 ? '(rupture)' : `(${getBarrelStock(bt?.value)} dispo)`}
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                              Type carton {line?.cartonInventoryId ? `(stock: ${getCartonStock(line?.cartonInventoryId)})` : ''}
                            </label>
                            <select value={line?.cartonInventoryId} onChange={e => updateLine(line?.id, 'cartonInventoryId', e?.target?.value)}
                              className="w-full border border-gray-300 dark:border-slate-600 rounded px-2 py-1.5 text-xs bg-white dark:bg-slate-700 text-gray-900 dark:text-white">
                              <option value="">-- Choisir --</option>
                              {cartonInventory?.map(ci => (
                                <option key={ci?.id} value={ci?.id} disabled={ci?.stockQuantity === 0}>
                                  {ci?.cartonType} {ci?.stockQuantity === 0 ? '(rupture)' : `(${ci?.stockQuantity} dispo)`}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Quantité</label>
                          <input type="number" min="1" value={line?.quantity} onChange={e => updateLine(line?.id, 'quantity', parseInt(e?.target?.value) || 1)}
                            className="w-full border border-gray-300 dark:border-slate-600 rounded px-2 py-1.5 text-xs bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Prix unitaire (€)</label>
                          <input type="number" min="0" step="0.01" value={line?.unitPrice} onChange={e => updateLine(line?.id, 'unitPrice', parseFloat(e?.target?.value) || 0)}
                            className="w-full border border-gray-300 dark:border-slate-600 rounded px-2 py-1.5 text-xs bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Sous-total</label>
                          <div className="px-2 py-1.5 text-xs font-semibold text-green-600 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                            {(line?.quantity * line?.unitPrice)?.toFixed(2)} €
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                <textarea value={form?.notes} onChange={e => setForm(f => ({ ...f, notes: e?.target?.value }))} rows={2}
                  className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
              </div>

              {/* Total */}
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Total commande</span>
                <span className="text-xl font-bold text-green-600">{orderTotal?.toFixed(2)} €</span>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {error}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-slate-700 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700">
                Annuler
              </button>
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium flex items-center gap-2">
                {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                {saving ? 'Enregistrement...' : 'Valider la commande'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
